import { useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, Star, Quote } from "lucide-react";
import { toast } from "sonner";
import {
  useTestimonialsAdmin,
  useUpsertTestimonial,
  useDeleteTestimonial,
  type Testimonial,
  type TestimonialInput,
} from "@/hooks/admin/useTestimonialsAdmin";

const emptyInput: TestimonialInput = {
  quote: "",
  author_name: "",
  author_title: "",
  rating: 5,
  sort_order: 0,
  is_active: true,
};

export default function TestimonialsAdminPage() {
  const { data: list, isLoading } = useTestimonialsAdmin();
  const upsert = useUpsertTestimonial();
  const remove = useDeleteTestimonial();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TestimonialInput>(emptyInput);
  const [open, setOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Testimonial | null>(null);

  function openNew() {
    setEditingId(null);
    setForm({ ...emptyInput, sort_order: (list?.length ?? 0) * 10 + 10 });
    setOpen(true);
  }

  function openEdit(t: Testimonial) {
    setEditingId(t.id);
    setForm({
      quote: t.quote,
      author_name: t.author_name,
      author_title: t.author_title ?? "",
      rating: t.rating ?? 5,
      sort_order: t.sort_order,
      is_active: t.is_active,
    });
    setOpen(true);
  }

  function handleSave() {
    if (!form.quote.trim() || !form.author_name.trim()) {
      toast.error("Quote and author are required");
      return;
    }
    const payload = {
      ...form,
      author_title: form.author_title?.trim() || null,
    };
    upsert.mutate(
      editingId ? { id: editingId, ...payload } : payload,
      {
        onSuccess: () => {
          toast.success(editingId ? "Testimonial updated" : "Testimonial added");
          setOpen(false);
        },
        onError: (e) => toast.error((e as Error).message || "Failed to save"),
      }
    );
  }

  function handleDelete() {
    if (!deleteTarget) return;
    remove.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success("Testimonial deleted");
        setDeleteTarget(null);
      },
      onError: (e) => toast.error((e as Error).message || "Failed to delete"),
    });
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground">Testimonials</h1>
            <p className="text-muted-foreground mt-1">
              Client quotes shown on the public site's home carousel.
            </p>
          </div>
          <Button onClick={openNew} className="shrink-0">
            <Plus className="h-4 w-4 mr-2" /> New testimonial
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : list && list.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No testimonials yet. Click "New testimonial" to add the first one.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {list?.map((t) => (
              <Card key={t.id} className={!t.is_active ? "opacity-60" : ""}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <Quote className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground leading-relaxed">{t.quote}</p>
                      <div className="mt-3 flex items-center gap-3 flex-wrap">
                        <span className="text-sm font-medium">{t.author_name}</span>
                        {t.author_title && (
                          <span className="text-xs text-muted-foreground">{t.author_title}</span>
                        )}
                        {t.rating != null && (
                          <div className="flex gap-0.5">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star
                                key={i}
                                className={`h-3.5 w-3.5 ${
                                  i < (t.rating ?? 0) ? "fill-amber-400 text-amber-400" : "text-border"
                                }`}
                              />
                            ))}
                          </div>
                        )}
                        <Badge variant="outline" className="text-[10px]">
                          order {t.sort_order}
                        </Badge>
                        {!t.is_active && <Badge variant="secondary">Hidden</Badge>}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="ghost" size="icon" aria-label="Edit testimonial" onClick={() => openEdit(t)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" aria-label="Delete testimonial" onClick={() => setDeleteTarget(t)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit testimonial" : "New testimonial"}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-2">
              <div className="grid gap-2">
                <Label htmlFor="t-quote">Quote</Label>
                <Textarea
                  id="t-quote"
                  rows={4}
                  value={form.quote}
                  onChange={(e) => setForm({ ...form, quote: e.target.value })}
                  placeholder="What the client said..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="t-name">Author name</Label>
                  <Input
                    id="t-name"
                    value={form.author_name}
                    onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                    placeholder="Sarah M."
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="t-title">Subtitle (optional)</Label>
                  <Input
                    id="t-title"
                    value={form.author_title ?? ""}
                    onChange={(e) => setForm({ ...form, author_title: e.target.value })}
                    placeholder="Google Review"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="t-rating">Rating (1-5)</Label>
                  <Input
                    id="t-rating"
                    type="number"
                    min={1}
                    max={5}
                    value={form.rating ?? 5}
                    onChange={(e) => setForm({ ...form, rating: Number(e.target.value) || null })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="t-sort">Sort order (lower = earlier)</Label>
                  <Input
                    id="t-sort"
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label htmlFor="t-active" className="text-sm font-medium">Visible on site</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Toggle off to hide without deleting.
                  </p>
                </div>
                <Switch
                  id="t-active"
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={upsert.isPending}>
                {upsert.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? "Save changes" : "Add testimonial"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete testimonial?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes "{deleteTarget?.quote.slice(0, 60)}..." from the database. Not recoverable.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={remove.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {remove.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
